import { useMutation, useQuery } from "@apollo/client";

import {
  APPROVE_PUBLIC_FINANCE_QUOTE_MUTATION,
  DOWNLOAD_PUBLIC_FINANCE_QUOTE_PDF_MUTATION,
  GET_PUBLIC_FINANCE_QUOTE_QUERY,
  REQUEST_PUBLIC_FINANCE_QUOTE_CHANGES_MUTATION,
} from "~/api";
import { errorsHandler } from "~/transformers";

export const useGetPublicFinanceQuote = (oemSlug?: string, token?: string) => {
  const { data, loading, error, refetch } = useQuery(
    GET_PUBLIC_FINANCE_QUOTE_QUERY,
    {
      skip: !oemSlug || !token,
      variables: { oemSlug, token },
      fetchPolicy: "network-only",
      // Keep the public page in sync when OEM edits status (e.g. draft → sent).
      pollInterval: 5000,
    },
  );

  return {
    quote: data?.getPublicFinanceQuote ?? null,
    loading,
    error,
    refetch,
  };
};

export const useDownloadPublicFinanceQuotePdf = () => {
  const [mutate, { loading }] = useMutation(
    DOWNLOAD_PUBLIC_FINANCE_QUOTE_PDF_MUTATION,
  );
  return {
    loading,
    downloadPdf: (oemSlug: string, token: string) =>
      new Promise<string>((resolve, reject) => {
        mutate({ variables: { oemSlug, token } })
          .then(({ data }) => resolve(data?.downloadPublicFinanceQuotePDF))
          .catch((err) => reject(errorsHandler(err)));
      }),
  };
};

export const useRequestPublicFinanceQuoteChanges = () => {
  const [mutate, { loading }] = useMutation(
    REQUEST_PUBLIC_FINANCE_QUOTE_CHANGES_MUTATION,
  );
  return {
    loading,
    requestChanges: (input: {
      oemSlug: string;
      token: string;
      message: string;
    }) =>
      new Promise((resolve, reject) => {
        mutate({ variables: { input } })
          .then(({ data }) => resolve(data?.requestPublicFinanceQuoteChanges))
          .catch((err) => reject(errorsHandler(err)));
      }),
  };
};

export const useApprovePublicFinanceQuote = () => {
  const [mutate, { loading }] = useMutation(
    APPROVE_PUBLIC_FINANCE_QUOTE_MUTATION,
  );
  return {
    loading,
    approveQuote: (input: Record<string, unknown>) =>
      new Promise((resolve, reject) => {
        mutate({ variables: { input } })
          .then(({ data }) => resolve(data?.approvePublicFinanceQuote))
          .catch((err) => reject(errorsHandler(err)));
      }),
  };
};
